import {type PropsWithChildren, useCallback, useEffect, useMemo, useState} from 'react'
import {UserSettingsContext} from './UserSettingsContext'
import {api} from '../../trpc/api'
import dayjs from 'dayjs'
import {getFirstDayOfWeek, type UserSettings} from '@package/shared'
import {FullscreenLoader} from '../../components/fullscreenLoader/FullscreenLoader.tsx'
import type {UpdateUserSettingsRequest} from '@app/api-resolver/router'
import {showNotification} from '../../notifications/showNotification.ts'
import {applyUserSettings} from './applyUserSettings.ts'

export const UserSettingsProvider = ({children}: PropsWithChildren) => {
  const defaultUserSettings = useMemo(() => {
    const localeName = new Intl.NumberFormat().resolvedOptions().locale
    const locale = new Intl.Locale(localeName)
    const is12Hours = !!new Intl.DateTimeFormat(undefined, {hour: 'numeric'}).format(0).match(/AM/)
    return {
      timezone: dayjs.tz.guess(),
      firstDayOfWeek: getFirstDayOfWeek(locale),
      timeFormat: is12Hours ? '12h' : '24h',
    } satisfies UserSettings
  }, [])

  const [initialized, setInitialized] = useState<boolean>(false)
  const {mutateAsync: getUserSettings, isPending: loading} = api.userSettings.get.useMutation()
  const [userSettings, setUserSettings] = useState<UserSettings>(defaultUserSettings)
  const [logoAnimationEnded, setLogoAnimationEnded] = useState<boolean>(false)

  useEffect(() => applyUserSettings(userSettings), [userSettings])

  useEffect(() => {
    getUserSettings({defaultValues: defaultUserSettings})
      .then((userSettings) => {
        applyUserSettings(userSettings)
        setUserSettings(userSettings)
      })
      .finally(() => setInitialized(true))
  }, [defaultUserSettings, getUserSettings])

  // update
  const {mutateAsync: updateUserSettings} = api.userSettings.update.useMutation(
    showNotification('Settings updated successfully', 'Error while updating settings'),
  )
  const handleUpdate = useCallback(
    async (request: UpdateUserSettingsRequest) => {
      const updatedUserSettings = await updateUserSettings(request)
      setUserSettings(updatedUserSettings)
    },
    [updateUserSettings],
  )

  const handleLogoAnimationEnded = useCallback(() => {
    setTimeout(() => setLogoAnimationEnded(true), 500)
  }, [])

  return (
    <UserSettingsContext.Provider
      value={{
        userSettings,
        loading: !initialized || loading,

        updateUserSettings: handleUpdate,
      }}
    >
      {initialized && logoAnimationEnded && children}
      <FullscreenLoader
        onLogoAnimationEnded={handleLogoAnimationEnded}
        loading={!initialized || !logoAnimationEnded || loading}
      />
    </UserSettingsContext.Provider>
  )
}
